import React, { useEffect, useRef, useState } from 'react';
import SlidePanel from '../../shared/SlidePanel';
import toast from '../../shared/Toast';
import { Icon } from '../BaileyBits';
import { hapticError, hapticSuccess, hapticTick } from '../../../_utils/haptics';
import ConfirmCard from './ConfirmCard';
import { errorText } from './api';

/**
 * Adding or changing something Bailey knows, in the app's slide panel with
 * its shared form fields. Adding: Cancel and Add answer. Changing: Remove on
 * the left (confirmed with the app's delete card) and Save on the right.
 */

// Questions clients ask that Beu can't answer from the account.
export const COMMON_QUESTIONS = ['Where can I park?', 'Do you do patch tests?', 'Can I bring my dog?', 'Is there step-free access?', 'Do you take cash?', 'Can I bring my children?'];

export function unanswered(answers) {
    const asked = new Set((answers || []).map((r) => String(r.question || '').trim().toLowerCase()));
    return COMMON_QUESTIONS.filter((q) => !asked.has(q.toLowerCase()));
}

export default function AnswerPanel({ answer = null, question = '', answers = [], onAdd, onChange, onRemove, onClose }) {
    const editing = !!answer;
    const sheet = useRef(null);
    const answerRef = useRef(null);
    const [q, setQ] = useState(editing ? answer.question || '' : question);
    const [a, setA] = useState(editing ? answer.answer || '' : '');
    const [busy, setBusy] = useState('');
    const [error, setError] = useState('');
    const [confirm, setConfirm] = useState(false);
    const [picked, setPicked] = useState('');
    const alive = useRef(true);
    useEffect(() => () => { alive.current = false; }, []);

    const close = () => { if (!alive.current) return; if (sheet.current) sheet.current.handleClose(); else onClose(); };
    const changed = !editing || q.trim() !== String(answer.question || '').trim() || a.trim() !== String(answer.answer || '').trim();
    const ready = !!q.trim() && !!a.trim() && changed && !busy;
    const suggestions = editing ? [] : unanswered(answers);

    const save = async () => {
        if (!ready) return;
        setBusy('save'); setError('');
        try {
            if (editing) await onChange(answer, { question: q.trim(), answer: a.trim() });
            else await onAdd({ question: q.trim(), answer: a.trim() });
            hapticSuccess();
            toast.success(editing ? 'Answer saved' : 'Answer added');
            close();
        } catch (e) {
            hapticError();
            // The new answer saved but the old one stayed: saving again would
            // add it twice, so say so and close.
            if (e && e.partial) { toast.error(e.message, 6000); close(); return; }
            if (!alive.current) return;
            setError(errorText(e, 'That answer didn’t save. Try again.'));
            setBusy('');
        }
    };
    const remove = async () => {
        setBusy('remove'); setError('');
        try {
            await onRemove(answer);
            hapticSuccess();
            toast.success('Answer removed');
            setConfirm(false);
            close();
        } catch (e) {
            hapticError();
            if (!alive.current) return;
            setConfirm(false);
            setError(errorText(e, 'That answer couldn’t be removed just now.'));
            setBusy('');
        }
    };
    const pick = (s) => {
        hapticTick();
        setQ(s);
        setPicked(s);
        requestAnimationFrame(() => { if (answerRef.current) answerRef.current.focus(); });
    };

    const footer = editing ? (
        <div className="fd-panel-footer">
            <button type="button" className="sp-btn-secondary" disabled={!!busy} onClick={() => { hapticTick(); setConfirm(true); }}>Remove</button>
            <button type="button" className="sp-btn-primary" disabled={!ready} onClick={save}>{busy === 'save' ? 'Saving…' : 'Save'}</button>
        </div>
    ) : (
        <div className="fd-panel-footer">
            <button type="button" className="sp-btn-secondary" onClick={close}>Cancel</button>
            <button type="button" className="sp-btn-primary" disabled={!ready} onClick={save}>{busy === 'save' ? 'Adding…' : 'Add answer'}</button>
        </div>
    );

    return (
        <SlidePanel ref={sheet} title={editing ? 'Edit answer' : 'Add an answer'} onClose={onClose} width={480} className="bly-panel fd-form" footer={footer}>
            <div className="sp-form">
                {error && <p className="sp-general-error" role="alert">{error}</p>}
                <div className="sp-field">
                    <label className="sp-label" htmlFor="fd-answer-q">What clients ask</label>
                    <input id="fd-answer-q" className="sp-input" value={q} maxLength={200} placeholder="e.g. Where can I park?" autoFocus={!editing && !question} onChange={(e) => setQ(e.target.value)} />
                    {!editing && !!picked && q === picked && (
                        <button type="button" className="sp-link" style={{ alignSelf: 'flex-start', marginTop: 8, background: 'none', border: 0, padding: 0, font: 'inherit', fontWeight: 600, textDecoration: 'underline', cursor: 'pointer', color: 'inherit' }} onClick={() => { hapticTick(); setQ(''); setPicked(''); }}>Choose a different question</button>
                    )}
                </div>
                {!!suggestions.length && !q.trim() && (
                    <div className="sp-field">
                        <span className="sp-label">Clients often ask</span>
                        <div className="fd-suggest">
                            {suggestions.map((s) => (
                                <button key={s} type="button" className="fd-suggest-row" onClick={() => pick(s)}>
                                    <span>{s}</span>
                                    <Icon.Plus size={16} />
                                </button>
                            ))}
                        </div>
                    </div>
                )}
                <div className="sp-field">
                    <label className="sp-label" htmlFor="fd-answer-a">My answer</label>
                    <textarea id="fd-answer-a" ref={answerRef} className="sp-textarea" rows={4} maxLength={600} value={a} autoFocus={!editing && !!question} placeholder="e.g. There’s free parking on Elm Street after 6pm, and a pay and display behind the salon." onChange={(e) => setA(e.target.value)} />
                    <p className="sp-hint">Prices, services, opening hours, free times and your team come straight from Beu, so there’s no need to add those.</p>
                </div>
            </div>
            {confirm && (
                <ConfirmCard
                    title="Remove this answer?"
                    text={`I’ll stop answering “${answer.question}” this way. This can’t be undone.`}
                    busy={busy === 'remove'}
                    onCancel={() => setConfirm(false)}
                    onConfirm={remove}
                />
            )}
        </SlidePanel>
    );
}
